/**
 * Shared quotation PDF document model — built from proposal DB snapshot, not DOM.
 * Hierarchy for PDF elevates services only so cables nest as supporting components.
 */
import type { DtoolsPortalPaymentTerm } from "./dtoolsDisplaySettings";
import {
  applyHierarchyOverridesToLines,
  elevateServiceLinesOnlyForQuotationPdf,
  readDtoolsHierarchyOverrides,
  sanitizeStagingLineFields,
} from "./dtoolsHierarchyOverrides";
import { buildReviewHierarchy, groupIdenticalProductsInReviewHierarchy } from "./dtoolsJsonStaging";
import {
  buildDtoolsPdfPackageCover,
  buildDtoolsPdfPackageOptions,
  formatDtoolsPdfPackageDate,
  resolveDtoolsPdfPackageRevisionNumber,
  type DtoolsPdfPackageCover,
} from "./dtoolsPdfPackage";
import {
  blankRepeatedScheduleRooms,
  buildDtoolsPdfScheduleLinesForSystem,
  type DtoolsPdfScheduleLine,
} from "./dtoolsPdfQuotationSchedule";
import { sortedProposalDisclaimers } from "./dtoolsProposalPresentation";
import {
  buildDtoolsStagingLinesFromProposalItems,
  proposalDbItemToStagingLine,
  resolveDtoolsProposalItemsForLiveView,
  type ProposalDbItemForDtoolsView,
  type ProposalHeaderForDtoolsView,
  buildDtoolsLiveProposalViewModel,
} from "./dtoolsProposalLiveView";
import { applyComposedScopeToStagingLines } from "./variationOrderApplyToLiveView";
import {
  enrichDtoolsReviewBuildContext,
  type DtoolsReviewBuildContext,
} from "./dtoolsReviewNavigation";
import { buildV2CustomerProposalView } from "./dtoolsV2CustomerProposalView";
import { resolveDtoolsCustomerTabGate } from "./dtoolsSystemClassification";
import { DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE } from "./dtoolsOptionVisibility";
import { withPaymentTermsDisplayFallback } from "./proposalCustomerPaymentTerms";
import {
  formatCustomerPricingDiscountLabel,
  resolveProposalCustomerPricingDisplay,
  sumProposalItemLineTotals,
  type ProposalCustomerPricingDisplay,
  type RetailPricingJson,
} from "./proposalCustomerPricing";
import { readRetailPricingJson } from "./dtoolsRetailPricingJson";
import {
  collectInternalSystemCustomerSections,
  DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID,
  sumVisibleDtoolsSystemsLineTotals,
} from "./dtoolsCustomerFacingDealTotal";
import { proposalPdfDownloadFilename, sanitizeProposalPdfDocumentTitle } from "./proposalPdfFilename";
import { formatLinkBExpiryLongLabel } from "./proposalLinkBExpiry";
import { isProposalAcceptedCommercialRecord } from "./proposalPublicAccess";
import {
  assertQuotationPdfRenderingIntegrity,
  resolveAuthoritativeSystemSchedulePricing,
} from "./quotationPdfPricingIntegrity";
export {
  QuotationPdfPricingIntegrityError,
  assertQuotationPdfRenderingIntegrity,
  resolveAuthoritativeSystemSchedulePricing,
  resolveAuthoritativeSystemScheduleTotal,
  resolveAuthoritativeCustomerFacingSystemPricing,
  resolveAuthoritativeCustomerFacingSystemTotal,
} from "./quotationPdfPricingIntegrity";

export type QuotationPdfScheduleDisplayLine = DtoolsPdfScheduleLine & { roomDisplay: string };

export type QuotationPdfOptionBlock = {
  key: string;
  title: string;
  sectionLabel: string;
  overview: string;
  highlights: string[];
  scheduleLines: QuotationPdfScheduleDisplayLine[];
  systemTotal: number;
  totalLabel: string;
};

/** Authoritative customer-facing commercial rows on the Proposal Summary page. */
export type QuotationPdfCommercialSummary = {
  proposalTotal: number;
  discountAmount: number;
  discountLabel: string;
  showDiscount: boolean;
  /** Formatted discount amount e.g. for tests; renderer formats from discountAmount. */
  finalPrice: number;
  finalPriceLabel: string;
  gstAmount: number;
  showGst: boolean;
  finalAmountPayable: number;
  showFinalAmountPayable: boolean;
  finalAmountPayableLabel: string;
};

export type QuotationPdfModel = {
  documentTitle: string;
  downloadFilename: string;
  cover: DtoolsPdfPackageCover;
  header: {
    customerName: string;
    address: string | null;
    proposalRef: string;
    revisionNumber: number;
    proposalDate: string;
  };
  /** Visible system/option rows only — never used as the payable Grand Total. */
  summaryRows: Array<{ label: string; total: number }>;
  /** Authoritative Proposal Total / Discount / Final Price (+ GST when enabled). */
  commercialSummary: QuotationPdfCommercialSummary | null;
  /** Payable / final commercial total (Final Price or Final Amount Payable). Not the visible-system sum. */
  grandTotal: number | null;
  grandTotalLabel: string;
  options: QuotationPdfOptionBlock[];
  showItemPricing: boolean;
  paymentTerms: DtoolsPortalPaymentTerm[];
  disclaimerParagraphs: string[];
  validityExpiryText: string;
  closing: {
    linkBUrl: string;
    proposalRef: string;
  };
  termsConditions: string[];
};

function renderStoredHighlights(highlights: string[]): string[] {
  const out: string[] = [];
  for (const raw of highlights) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) continue;
    if (trimmed.includes("\n")) {
      for (const line of trimmed.split("\n")) {
        const item = line.replace(/^[\s*•✓\-–—]+/, "").trim();
        if (item) out.push(item);
      }
      continue;
    }
    out.push(trimmed);
  }
  return out;
}

const DEFAULT_TERMS: string[] = [
  "All prices are stated in Singapore Dollars (SGD).",
  "Clarity's Terms & Conditions of Sale shall apply unless otherwise agreed in writing. See claritygroup.com.sg/terms/retail.",
  "Minor Installation Accessories — Clarity may supply and charge for minor accessories reasonably required to complete the installation, up to the lower of 0.5% of the Proposal’s Final Price or S$500, without further approval. Full terms: https://portal.claritygroup.com.sg/terms/retail#minor-installation-accessories",
  "Labour, programming, and commissioning are included only where listed as separate line items.",
  "Custom design and engineering work is scoped separately unless included above.",
];

/**
 * Build review systems for quotation PDF: services elevated, cables keep parentId nesting.
 * When variationOrderScope is provided, uses the same composed living contract as Preview Proposal.
 */
export function buildQuotationPdfReviewSystems(
  header: ProposalHeaderForDtoolsView,
  items: ProposalDbItemForDtoolsView[],
  options?: { variationOrderScope?: import("./variationOrderCurrentScope").ComposedProjectScope | null },
) {
  const resolvedItems = resolveDtoolsProposalItemsForLiveView(header, items);
  const overrides = readDtoolsHierarchyOverrides(header.parsedMetadataJson);
  const rawStagingLines = buildDtoolsStagingLinesFromProposalItems(resolvedItems, header.parsedMetadataJson);
  let stagingLines = applyHierarchyOverridesToLines(
    elevateServiceLinesOnlyForQuotationPdf(sanitizeStagingLineFields(rawStagingLines), overrides),
    overrides,
  );
  const variationOrderScope = options?.variationOrderScope ?? null;
  if (variationOrderScope) {
    stagingLines = applyComposedScopeToStagingLines(stagingLines, variationOrderScope);
  }
  if (stagingLines.length === 0) return null;
  return groupIdenticalProductsInReviewHierarchy(buildReviewHierarchy(stagingLines));
}

export type BuildQuotationPdfModelInput = {
  header: ProposalHeaderForDtoolsView & {
    id: number;
    createdAt?: string | Date | null;
    linkBExpiresAt?: string | Date | null;
    revisionNumber?: number | null;
    projectId?: number | null;
    proposalSource?: string | null;
    originalProposalStorageKey?: string | null;
    retailPricingJson?: unknown;
    invoiceGstEnabled?: boolean | null;
    approvedAt?: string | Date | null;
    status?: string | null;
    pipelineStage?: string | null;
    hasTradeInItems?: boolean | null;
    tradeInItemsText?: string | null;
  };
  items: ProposalDbItemForDtoolsView[];
  linkBUrl: string;
  address?: string | null;
  /** Living Commercial Head scope (approved + approved VOs). Same as Preview Proposal. */
  variationOrderScope?: import("./variationOrderCurrentScope").ComposedProjectScope | null;
  /**
   * Living/revised contract value from approved VOs.
   * When set, this is the payable total — original retail discounts must not be re-applied.
   */
  grandTotal?: number | null;
  /**
   * Render-only override for hide/show individual item pricing.
   * Does not mutate proposal metadata — affects this PDF model only.
   */
  hideItemPricingOverride?: boolean;
};

/** Apply a one-shot PDF render override without mutating saved display settings. */
export function applyHideItemPricingOverride(
  displaySettings: import("./dtoolsDisplaySettings").DtoolsDisplaySettings,
  override?: boolean,
): import("./dtoolsDisplaySettings").DtoolsDisplaySettings {
  if (typeof override !== "boolean") return displaySettings;
  return { ...displaySettings, hideItemPricing: override };
}

export function buildQuotationPdfModel(input: BuildQuotationPdfModelInput): QuotationPdfModel | null {
  const { header, items, linkBUrl, variationOrderScope } = input;
  const hierarchy = buildQuotationPdfReviewSystems(header, items, { variationOrderScope });
  if (!hierarchy || hierarchy.systems.length === 0) {
    // Fall back to live view (may elevate cables) only if PDF-specific path empty — rare.
    const live = buildDtoolsLiveProposalViewModel(header, items, { variationOrderScope });
    if (!live || live.systems.length === 0) return null;
    return buildQuotationPdfModelFromLiveSystems({
      header,
      items,
      systems: live.systems,
      footer: live.footer,
      presentation: live.presentation,
      paymentTerms: live.paymentTerms,
      displaySettings: applyHideItemPricingOverride(
        live.displaySettings,
        input.hideItemPricingOverride,
      ),
      tabModelVersion: live.tabModelVersion,
      systemClassifications: live.systemClassifications,
      parsedMetadataJson: header.parsedMetadataJson,
      linkBUrl,
      address: input.address,
      createdAt: header.createdAt,
      linkBExpiresAt: header.linkBExpiresAt,
      revisionNumber: header.revisionNumber,
      grandTotal:
        input.grandTotal ??
        variationOrderScope?.revisedContractValue ??
        null,
      variationOrderScope,
    });
  }

  const liveMeta = buildDtoolsLiveProposalViewModel(header, items, { variationOrderScope });
  return buildQuotationPdfModelFromLiveSystems({
    header,
    items,
    systems: hierarchy.systems,
    footer: hierarchy.footer,
    presentation: liveMeta?.presentation,
    paymentTerms: liveMeta?.paymentTerms ?? [],
    displaySettings: applyHideItemPricingOverride(
      liveMeta?.displaySettings ?? {},
      input.hideItemPricingOverride,
    ),
    tabModelVersion: liveMeta?.tabModelVersion,
    systemClassifications: liveMeta?.systemClassifications,
    parsedMetadataJson: header.parsedMetadataJson,
    linkBUrl,
    address: input.address,
    createdAt: header.createdAt,
    linkBExpiresAt: header.linkBExpiresAt,
    revisionNumber: header.revisionNumber,
    grandTotal:
      input.grandTotal ??
      variationOrderScope?.revisedContractValue ??
      null,
    variationOrderScope,
  });
}

function roundMoneyLocal(n: number): number {
  return Math.round(n * 100) / 100;
}

function buildCommercialSummaryFromPricing(
  pricing: ProposalCustomerPricingDisplay,
): QuotationPdfCommercialSummary {
  return {
    proposalTotal: pricing.proposalTotal,
    discountAmount: pricing.discountAmount,
    discountLabel: formatCustomerPricingDiscountLabel(pricing),
    showDiscount: pricing.showDiscount,
    finalPrice: pricing.finalAgreedPrice,
    finalPriceLabel: "Final Price",
    gstAmount: pricing.gstAmount,
    showGst: pricing.showGst,
    finalAmountPayable: pricing.finalAmountPayable,
    showFinalAmountPayable: pricing.showFinalAmountPayable,
    finalAmountPayableLabel: "Final Amount Payable",
  };
}

/** Complete validity statement for PDF terms (not a bare date). */
export function resolveQuotationPdfValidityStatement(input: {
  header: {
    approvedAt?: string | Date | null;
    status?: string | null;
    pipelineStage?: string | null;
    linkBExpiresAt?: string | Date | null;
  };
  linkBExpiresAt?: string | Date | null;
}): string {
  if (isProposalAcceptedCommercialRecord(input.header)) {
    return "This accepted proposal record remains available online.";
  }
  const expiry = formatLinkBExpiryLongLabel(
    input.linkBExpiresAt ?? input.header.linkBExpiresAt,
  );
  if (!expiry) return "";
  return `This proposal is valid until ${expiry}.`;
}

/**
 * PDF commercial authority (presentation only — does not mutate stored pricing):
 * - Proposal Total = customer-visible/selected D-Tools systems on the PDF.
 * - Final Price = living VO revised contract value when present, else stored agreed price.
 * - Discount = Proposal Total − Final Price (unselected options are omitted, not discounted).
 * - Living VO contract value is already final — do not re-apply stored specialDiscountSgd.
 */
export function resolveQuotationPdfCommercialAuthority(input: {
  header: BuildQuotationPdfModelInput["header"];
  items: ProposalDbItemForDtoolsView[];
  /** Living VO contract — when present, do not re-apply original discounts. */
  livingContractValue: number | null;
  /** Selected/visible PDF scope total (never raw all-option listTotal). */
  customerScopeProposalTotal: number;
}): {
  commercialSummary: QuotationPdfCommercialSummary | null;
  grandTotal: number | null;
  grandTotalLabel: string;
} {
  const living = input.livingContractValue;
  const hasLivingVoContract = living != null && Number.isFinite(living);
  const customerScopeProposalTotal = roundMoneyLocal(
    Math.max(0, Number(input.customerScopeProposalTotal) || 0),
  );

  const stored = readRetailPricingJson(
    (input.header as { retailPricingJson?: unknown }).retailPricingJson,
  );
  // In-memory effective pricing only — never written back to retailPricingJson / DB.
  const effectivePricing: RetailPricingJson = {
    ...(stored ?? {}),
    listTotal: customerScopeProposalTotal,
    originalListPrice: customerScopeProposalTotal,
  };

  if (hasLivingVoContract) {
    effectivePricing.agreedPriceSgd = roundMoneyLocal(Number(living));
    effectivePricing.specialDiscountSgd = null;
  }

  const lineItemTotalSum = sumProposalItemLineTotals(input.items);
  const pricing = resolveProposalCustomerPricingDisplay({
    totalAmount: hasLivingVoContract
      ? roundMoneyLocal(Number(living))
      : input.header.totalAmount,
    retailPricingJson: effectivePricing,
    invoiceGstEnabled: (input.header as { invoiceGstEnabled?: boolean | null }).invoiceGstEnabled,
    computedProposalTotal: customerScopeProposalTotal,
    lineItemTotalSum,
  });
  const commercialSummary = buildCommercialSummaryFromPricing(pricing);
  const grandTotal = pricing.showGst
    ? pricing.finalAmountPayable
    : pricing.showFinalAgreedPrice
      ? pricing.finalAgreedPrice
      : pricing.proposalTotal;
  const grandTotalLabel = pricing.showGst
    ? "Final Amount Payable"
    : pricing.showFinalAgreedPrice
      ? "Final Price"
      : "Grand Total";
  return { commercialSummary, grandTotal, grandTotalLabel };
}

function buildQuotationPdfModelFromLiveSystems(input: {
  header: BuildQuotationPdfModelInput["header"];
  items: ProposalDbItemForDtoolsView[];
  systems: import("./dtoolsStagingTransform").DtoolsReviewSystem[];
  footer: import("./dtoolsStagingTransform").DtoolsReviewFooter;
  presentation?: import("./dtoolsProposalPresentation").DtoolsProposalPresentation | null;
  paymentTerms: DtoolsPortalPaymentTerm[];
  displaySettings: import("./dtoolsDisplaySettings").DtoolsDisplaySettings;
  tabModelVersion?: import("./dtoolsSystemClassification").DtoolsTabModelVersion;
  systemClassifications?: import("./dtoolsSystemClassification").DtoolsSystemClassificationsMap;
  parsedMetadataJson?: unknown;
  linkBUrl: string;
  address?: string | null;
  createdAt?: string | Date | null;
  linkBExpiresAt?: string | Date | null;
  revisionNumber?: number | null;
  grandTotal?: number | null;
  variationOrderScope?: import("./variationOrderCurrentScope").ComposedProjectScope | null;
}): QuotationPdfModel {
  const {
    header,
    items,
    systems,
    presentation,
    paymentTerms,
    displaySettings,
    tabModelVersion,
    systemClassifications,
    parsedMetadataJson,
    linkBUrl,
    address,
    createdAt,
    linkBExpiresAt,
    revisionNumber,
    grandTotal: grandTotalInput,
  } = input;

  const tabGate = resolveDtoolsCustomerTabGate({
    systems,
    tabModelVersion,
    parsedMetadataJson,
    systemClassifications,
  });
  const enrichedContext = enrichDtoolsReviewBuildContext(systems, {
    presentation: presentation ?? undefined,
    systemClassifications: tabGate.systemClassifications,
  });
  const isV2 = tabGate.useV2TabModel;
  const v2View = isV2
    ? buildV2CustomerProposalView({
        systems,
        classifications: tabGate.systemClassifications,
        displaySettings,
        reviewContext: enrichedContext,
        audience: DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE,
      })
    : null;

  const proposalRef = String(header.proposalRef ?? "").trim() || "proposal";
  const cover = buildDtoolsPdfPackageCover({
    customerName: String(header.customerNameSnapshot ?? "").trim() || "Valued Customer",
    proposalTitle: String(header.proposalName ?? "").trim() || "Proposal",
    proposalRef,
    revisionNumber: revisionNumber ?? null,
    proposalDate: createdAt ?? null,
    heroImageUrl: presentation?.heroImageUrl,
  });

  const pdfOptions = buildDtoolsPdfPackageOptions({
    isV2,
    systems,
    v2View,
    displaySettings,
    reviewContext: enrichedContext,
    audience: DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE,
  });

  const showItemPricing = displaySettings.hideItemPricing !== true;
  const options: QuotationPdfOptionBlock[] = pdfOptions.map((opt, index) => {
    // System/schedule authority only — not Final Agreed Price / payable.
    const pricing = resolveAuthoritativeSystemSchedulePricing(
      opt.system,
      enrichedContext,
      opt.hierarchyIndex ?? index,
    );
    const scheduleRaw = buildDtoolsPdfScheduleLinesForSystem(opt.system, opt.sectionLabel);
    const scheduleLines = blankRepeatedScheduleRooms(scheduleRaw);
    return {
      key: opt.key,
      title: opt.title,
      sectionLabel: opt.sectionLabel,
      overview: String(pricing.view.overview.description ?? "").trim(),
      highlights: renderStoredHighlights(pricing.view.overview.highlights ?? []),
      scheduleLines,
      systemTotal: pricing.systemTotal,
      totalLabel: opt.isAlternative ? "Option Total" : "System Total",
    };
  });

  // Priced lines parked in internal "Unassigned"/"Unknown" systems are always customer-billable:
  // services under "Services", everything else under "Other items" (same helper as the Grand Total).
  for (const [serviceIndex, serviceSystem] of collectInternalSystemCustomerSections(
    systems,
    displaySettings,
  ).entries()) {
    const pricing = resolveAuthoritativeSystemSchedulePricing(
      serviceSystem,
      enrichedContext,
      pdfOptions.length + serviceIndex,
    );
    const label = serviceSystem.name || "Services";
    const isOtherItems = serviceSystem.systemId === DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID;
    const scheduleLines = blankRepeatedScheduleRooms(
      buildDtoolsPdfScheduleLinesForSystem(serviceSystem, label),
    );
    options.push({
      key: isOtherItems ? `global-other-items-${serviceIndex}` : `global-services-${serviceIndex}`,
      title: label,
      sectionLabel: label,
      overview: "",
      highlights: [],
      scheduleLines,
      systemTotal: pricing.systemTotal,
      totalLabel: `${label} Total`,
    });
  }

  // Rendering integrity: visible bundled schedule Σ must equal the system/schedule authority.
  // Negotiated Final Agreed Price is a separate commercial layer and must not fail this.
  assertQuotationPdfRenderingIntegrity({
    options: options.map((o) => ({
      key: o.key,
      authoritativeSystemTotal: o.systemTotal,
      scheduleLines: o.scheduleLines,
    })),
    showItemPricing,
  });

  const summaryRows = options.map((o) => ({ label: o.sectionLabel, total: o.systemTotal }));
  // Same selected/visible-scope authority as PDF sections (excludes hiddenOptionKeys).
  const customerScopeProposalTotal = sumVisibleDtoolsSystemsLineTotals(
    systems,
    displaySettings,
  );
  const livingContractValue =
    grandTotalInput != null && Number.isFinite(Number(grandTotalInput))
      ? Number(grandTotalInput)
      : null;
  const { commercialSummary, grandTotal, grandTotalLabel } =
    resolveQuotationPdfCommercialAuthority({
      header,
      items,
      livingContractValue,
      customerScopeProposalTotal,
    });

  return {
    documentTitle: sanitizeProposalPdfDocumentTitle(proposalRef),
    downloadFilename: proposalPdfDownloadFilename(proposalRef),
    cover,
    header: {
      customerName: cover.customerName,
      address: address?.trim() || null,
      proposalRef: cover.proposalRef,
      revisionNumber: resolveDtoolsPdfPackageRevisionNumber(revisionNumber),
      proposalDate: formatDtoolsPdfPackageDate(createdAt),
    },
    summaryRows,
    commercialSummary,
    grandTotal,
    grandTotalLabel,
    options,
    showItemPricing,
    paymentTerms: withPaymentTermsDisplayFallback(paymentTerms),
    disclaimerParagraphs: [
      ...(header.hasTradeInItems === true && String(header.tradeInItemsText ?? "").trim()
        ? [`CUSTOMER TRADE-IN ITEMS: ${String(header.tradeInItemsText).trim()}`]
        : []),
      ...sortedProposalDisclaimers(presentation ?? undefined)
        .map((d) => String(d.content ?? "").trim())
        .filter(Boolean),
    ],
    validityExpiryText: resolveQuotationPdfValidityStatement({
      header,
      linkBExpiresAt,
    }),
    closing: {
      linkBUrl: String(linkBUrl ?? "").trim(),
      proposalRef: cover.proposalRef,
    },
    termsConditions: DEFAULT_TERMS,
  };
}

/** @internal Test helper — map raw staging lines through PDF elevation + hierarchy. */
export function buildQuotationPdfHierarchyFromStagingLines(
  lines: import("./dtoolsJsonStaging").DtoolsStagingLine[],
  overrides?: import("./dtoolsHierarchyOverrides").DtoolsHierarchyOverrides | null,
) {
  const stagingLines = applyHierarchyOverridesToLines(
    elevateServiceLinesOnlyForQuotationPdf(sanitizeStagingLineFields(lines), overrides),
    overrides ?? undefined,
  );
  return groupIdenticalProductsInReviewHierarchy(buildReviewHierarchy(stagingLines));
}

export { proposalDbItemToStagingLine };
